import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import {
  ShiftSummary,
  ShiftSummaryAttachment,
  ShiftSummaryClient,
  ShiftSummaryPartCTestStatus,
  UserNameViewModel,
  VendorJob,
  VendorJobClient
} from '../../../services/nswag/nswagclient';
import { BlockBlobClient } from '@azure/storage-blob';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { FileUpload } from 'primeng/fileupload';
import { FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';
import { MultiSelectModule } from 'primeng/multiselect';
import { PanelModule } from 'primeng/panel';
import { Popover } from 'primeng/popover';
import { PopoverModule } from 'primeng/popover';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';
import {
  buildPersonnelOptions,
  buildShiftShortcutOptions,
  buildShiftSummaryPayload,
  formatPartCTestStatus as formatPartCTestStatusLabel,
  getFluidTypeLabel,
  getFluidTypeOptions,
  getShiftTimeWarningMessages,
  isImageAttachment,
  isDrilloutWashoutProfile,
  normalizeSelectedFiles,
  PART_C_TEST_STATUS_VALUES,
  requiresFoamAndSandMetrics as requiresFoamAndSandMetricsForFluidSystem,
  resolveShiftSummaryProfile,
  ShiftSummaryCrewMemberOption,
  ShiftSummaryPartCTestStatusSelection,
  ShiftShortcutOption,
  showsPartCDetails,
  supportsCurrentFluidSystem as supportsCurrentFluidSystemForProfile,
  supportsFoamMetrics as supportsFoamMetricsForProfile,
  validateShiftSummaryForSave,
  validateShiftSummaryForSubmit
} from './shift-summary-create.helpers';
import { UserClient } from '../../../services/nswag/nswagclient';
import { ChemicalDosageChartComponent } from '../chemical-dosage-chart/chemical-dosage-chart';

interface PendingAttachment {
  id: string;
  file: File;
  description: string;
  isImage: boolean;
  previewUrl: string | null;
}

type RemoveFileCallback = (event: Event, index: number) => void;

@Component({
  selector: 'app-shift-summary-create',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DatePickerModule,
    FileUploadModule,
    InputNumberModule,
    MessageModule,
    MultiSelectModule,
    PanelModule,
    PopoverModule,
    ProgressSpinnerModule,
    RadioButtonModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    TooltipModule,
    ChemicalDosageChartComponent
  ],
  templateUrl: './shift-summary-create.component.html',
  styleUrls: ['./shift-summary-create.component.css']
})
export class ShiftSummaryCreateComponent implements OnInit, OnDestroy {
  @ViewChild(FileUpload) fileUploader?: FileUpload;

  jobId = '';
  shiftSummaryId: string | null = null;

  loading = signal(false);
  saving = signal(false);
  submitting = signal(false);
  validationError = signal<string | null>(null);

  currentSummary = signal<ShiftSummary | null>(null);
  pendingAttachments = signal<PendingAttachment[]>([]);
  savedAttachments = signal<ShiftSummaryAttachment[]>([]);
  savedAttachmentPreviewUrls = signal<Record<string, string>>({});
  savingAttachmentIds = signal<string[]>([]);
  shiftShortcutOptions = signal<ShiftShortcutOption[]>([]);
  crewMemberOptions = signal<SelectItem[]>([]);
  jobWorkbookType = signal<string | null>(null);

  readonly isReadOnly = computed(() => this.currentSummary()?.isSubmitted ?? false);
  readonly isExisting = computed(() => !!this.shiftSummaryId);
  readonly activeSummaryProfile = computed(() =>
    this.currentSummary()?.summaryProfile ?? resolveShiftSummaryProfile(this.jobWorkbookType())
  );
  readonly fluidTypeOptions = computed(() => getFluidTypeOptions(this.activeSummaryProfile()));
  readonly fluidTypeLabel = computed(() => getFluidTypeLabel(this.activeSummaryProfile()));
  readonly title = computed(() => {
    if (this.isReadOnly()) {
      return 'Shift Summary (Submitted)';
    }

    return this.isExisting() ? 'Shift Summary (Draft)' : 'New Shift Summary';
  });

  // Form state
  shiftStartUtc: Date | null = null;
  shiftEndUtc: Date | null = null;
  dayNight = 'Day';
  selectedCrewMemberUserIds: string[] = [];
  currentStatus = '';
  safetyTopicsDiscussed = '';
  anySafetyIncidents = false;
  safetyIncidentDetails = '';
  safetyIncidents: string[] = [];
  jobStatus = '';
  customerFeedback = '';
  mechanicalNeeds = '';
  nextPlannedWork = '';
  dataComponentsWorking = false;
  dataComponentsContext = '';
  fluidType = '';
  currentFluidSystem = '';
  foamStabilityMinutes: number | null = null;
  sandSuspensionMinutes: number | null = null;
  readonly partCTestStatusValues = PART_C_TEST_STATUS_VALUES;
  partCTestStatus: ShiftSummaryPartCTestStatusSelection | '' = '';
  gramsToPass: number | null = null;
  waterLeakOff: number | null = null;
  iscavGallonsConsumed: number | null = null;
  procideGallonsConsumed: number | null = null;
  fuelProvidedByCustomer = false;

  private crewMemberPersonnelOptions: ShiftSummaryCrewMemberOption[] = [];
  private pendingAttachmentSequence = 0;

  dayNightOptions = [
    { label: 'Day', value: 'Day' },
    { label: 'Night', value: 'Night' }
  ];

  statusOptions = [
    { label: 'Rigging up', value: 'Rigging up' },
    { label: 'Running', value: 'Running' },
    { label: 'Rigging Down', value: 'Rigging Down' },
    { label: 'On Standby', value: 'On Standby' }
  ];

  currentFluidSystemOptions = [
    { label: 'HydroLyft', value: 'HydroLyft' },
    { label: 'TurboLyft', value: 'TurboLyft' },
    { label: 'Slickwater', value: 'Slickwater' }
  ];

  incidentOptions = [
    { label: 'Spilled working water', value: 'Spilled working water' },
    { label: 'Spilled fresh water', value: 'Spilled fresh water' },
    { label: 'Spilled chemical', value: 'Spilled chemical' },
    { label: 'Dangerous weather', value: 'Dangerous weather' },
    { label: 'Injury on location', value: 'Injury on location' },
    { label: 'Travel incident to or from pad', value: 'Travel incident to or from pad' },
    { label: 'Equipment malfunction', value: 'Equipment malfunction' },
    { label: 'Safety equipment out of date/faulty', value: 'Safety equipment out of date/faulty' },
    { label: 'Hazardous gas alarm triggered', value: 'Hazardous gas alarm triggered' },
    { label: 'CTU/WOR incident', value: 'CTU/WOR incident' },
    { label: 'Safety Standdown', value: 'Safety Standdown' }
  ];

  private readonly shiftShortcutLabelFormatter = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly shiftSummaryClient: ShiftSummaryClient,
    private readonly vendorJobClient: VendorJobClient,
    private readonly userClient: UserClient,
    private readonly confirmationService: ConfirmationService
  ) { }

  async ngOnInit(): Promise<void> {
    this.jobId = this.route.snapshot.paramMap.get('id') ?? '';
    this.shiftSummaryId = this.route.snapshot.paramMap.get('shiftSummaryId');

    await this.loadJobContext();

    if (this.shiftSummaryId) {
      await this.loadSummary(this.shiftSummaryId);
    } else {
      this.refreshPersonnelOptions();
    }
  }

  ngOnDestroy(): void {
    this.clearPendingAttachments();
  }

  async saveDraft(): Promise<void> {
    if (this.isReadOnly()) {
      return;
    }

    if (!this.validateForSave()) {
      return;
    }

    const shouldContinue = await this.confirmShiftTimeIfNeeded('Save Draft');
    if (!shouldContinue) {
      return;
    }

    await this.saveDraftInternal();
  }

  async submit(): Promise<void> {
    if (this.isReadOnly()) {
      return;
    }

    if (!this.validateForSubmit()) {
      return;
    }

    const shouldContinue = await this.confirmShiftTimeIfNeeded('Submit');
    if (!shouldContinue) {
      return;
    }

    if (!this.hasAnyAttachments()) {
      this.confirmationService.confirm({
        key: 'confirmationDialog',
        header: 'Submit Without Attachments?',
        message: 'This shift summary does not have any files attached. Do you want to submit it anyway?',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Submit',
        rejectLabel: 'Cancel',
        accept: () => {
          void this.submitValidated();
        }
      });
      return;
    }

    await this.submitValidated();
  }

  private async submitValidated(): Promise<void> {
    this.submitting.set(true);
    try {
      const saved = await this.saveDraftInternal(false);
      if (!saved?.id) {
        return;
      }

      const submitted = await firstValueFrom(this.shiftSummaryClient.submit(saved.id));
      this.currentSummary.set(submitted);
      this.shiftSummaryId = submitted.id ?? this.shiftSummaryId;
      this.mapSummaryToForm(submitted);
      this.setSavedAttachments(submitted.attachments);
      await this.loadSavedAttachmentPreviewUrls(submitted.attachments);
      this.validationError.set(null);

      if (this.shiftSummaryId) {
        await this.router.navigate(['/vendorjob', this.jobId, 'shiftsummaries', this.shiftSummaryId, 'view']);
      }
    } catch (error) {
      console.error('Error submitting shift summary', error);
      this.validationError.set('Unable to submit shift summary. Please try again.');
    } finally {
      this.submitting.set(false);
    }
  }

  onShiftStartChanged(newStart: Date | null): void {
    this.shiftStartUtc = newStart;
    if (newStart && !this.shiftEndUtc) {
      this.shiftEndUtc = this.addHours(newStart, 12);
    }
  }

  onDayNightChanged(value: string | null | undefined): void {
    this.dayNight = value ?? 'Day';
    this.refreshPersonnelOptions();
  }

  supportsFoamMetrics(): boolean {
    return supportsFoamMetricsForProfile(this.activeSummaryProfile());
  }

  supportsCurrentFluidSystem(): boolean {
    return supportsCurrentFluidSystemForProfile(this.activeSummaryProfile());
  }

  requiresFoamAndSandMetrics(): boolean {
    return requiresFoamAndSandMetricsForFluidSystem(this.currentFluidSystem);
  }

  fluidTypeOutputLabel(): string {
    return isDrilloutWashoutProfile(this.activeSummaryProfile())
      ? 'Recirc or Fresh Water?'
      : 'Base or Recirc Fluid?';
  }

  showPartCDetails(): boolean {
    return showsPartCDetails(this.partCTestStatus);
  }

  showPartCDetailsForValue(value: ShiftSummaryPartCTestStatus | undefined | null): boolean {
    return showsPartCDetails(value);
  }

  formatPartCTestStatus(value: ShiftSummaryPartCTestStatus | undefined | null): string {
    return formatPartCTestStatusLabel(value);
  }

  openShiftShortcutPopover(event: Event | { originalEvent?: Event }, popover: Popover): void {
    if (this.isReadOnly()) {
      return;
    }

    this.shiftShortcutOptions.set(buildShiftShortcutOptions(
      new Date(),
      date => this.formatShiftShortcutDate(date)
    ));
    const anchorEvent = (event as { originalEvent?: Event }).originalEvent ?? (event as Event);
    popover.toggle(anchorEvent);
  }

  applyShiftShortcut(option: ShiftShortcutOption, popover: Popover): void {
    if (this.isReadOnly()) {
      return;
    }

    this.shiftStartUtc = new Date(option.shiftStartUtc);
    this.shiftEndUtc = new Date(option.shiftEndUtc);
    this.validationError.set(null);
    popover.hide();
  }

  setShiftEndToTwelveHoursPastStart(): void {
    if (!this.shiftStartUtc || this.isReadOnly()) {
      return;
    }

    this.shiftEndUtc = this.addHours(this.shiftStartUtc, 12);
  }

  onFilesSelected(event: { files?: File[] | ArrayLike<File> | null }): void {
    const newAttachments = normalizeSelectedFiles(event.files).map(file => this.createPendingAttachment(file));
    this.pendingAttachments.update(existing => [...existing, ...newAttachments]);
  }

  onFileRemoved(event: { file: File }): void {
    this.removePendingAttachmentByFile(event.file);
  }

  removePendingAttachment(
    event: Event | { originalEvent?: Event },
    index: number,
    file: File,
    removeFileCallback?: RemoveFileCallback
  ): void {
    const originalEvent = (event as { originalEvent?: Event }).originalEvent ?? (event as Event);
    if (removeFileCallback) {
      removeFileCallback(originalEvent, index);
      return;
    }

    this.removePendingAttachmentByFile(file);
  }

  updatePendingAttachmentDescription(pendingAttachmentId: string, description: string): void {
    this.pendingAttachments.update(attachments =>
      attachments.map(attachment =>
        attachment.id === pendingAttachmentId
          ? { ...attachment, description }
          : attachment
      ));
  }

  updatePendingAttachmentDescriptionForFile(file: File, description: string): void {
    const existing = this.getPendingAttachment(file);
    if (existing) {
      this.updatePendingAttachmentDescription(existing.id, description);
      return;
    }

    const created = this.createPendingAttachment(file);
    created.description = description;
    this.pendingAttachments.update(attachments => [...attachments, created]);
  }

  getPendingAttachment(file: File): PendingAttachment | undefined {
    return this.pendingAttachments().find(attachment => this.areFilesEqual(attachment.file, file));
  }

  getPendingAttachmentDescription(file: File): string {
    return this.getPendingAttachment(file)?.description ?? '';
  }

  isPendingAttachmentImage(file: File): boolean {
    return this.getPendingAttachment(file)?.isImage ?? isImageAttachment(file.type, file.name);
  }

  getPendingAttachmentPreviewUrl(file: File): string | null {
    return this.getPendingAttachment(file)?.previewUrl ?? null;
  }

  isSavedAttachmentImage(attachment: ShiftSummaryAttachment): boolean {
    return isImageAttachment(attachment.contentType, attachment.fileName);
  }

  getSavedAttachmentPreviewUrl(attachment: ShiftSummaryAttachment): string | null {
    return attachment.id ? (this.savedAttachmentPreviewUrls()[attachment.id] ?? null) : null;
  }

  hasSavedAttachmentDescriptionChanged(attachment: ShiftSummaryAttachment): boolean {
    const original = this.currentSummary()?.attachments?.find(item => item.id === attachment.id);
    return (original?.description ?? '') !== (attachment.description ?? '');
  }

  isSavingAttachmentDescription(attachmentId: string | undefined): boolean {
    return !!attachmentId && this.savingAttachmentIds().includes(attachmentId);
  }

  async saveAttachmentDescription(attachment: ShiftSummaryAttachment): Promise<void> {
    if (this.isReadOnly() || !this.shiftSummaryId || !attachment.id || this.isSavingAttachmentDescription(attachment.id)) {
      return;
    }

    this.savingAttachmentIds.update(ids => [...ids, attachment.id!]);
    try {
      const payload = new ShiftSummaryAttachment();
      payload.description = attachment.description?.trim() || undefined;

      const updated = await firstValueFrom(
        this.shiftSummaryClient.updateAttachment(this.shiftSummaryId, attachment.id, payload)
      );
      this.currentSummary.set(updated);
      this.setSavedAttachments(updated.attachments);
      await this.loadSavedAttachmentPreviewUrls(updated.attachments);
      this.validationError.set(null);
    } catch (error) {
      console.error('Error saving attachment description', error);
      this.validationError.set('Unable to save attachment description.');
    } finally {
      this.savingAttachmentIds.update(ids => ids.filter(id => id !== attachment.id));
    }
  }

  async removeAttachment(attachmentId: string | undefined): Promise<void> {
    if (this.isReadOnly() || !this.shiftSummaryId || !attachmentId) {
      return;
    }

    try {
      const updated = await firstValueFrom(this.shiftSummaryClient.removeAttachment(this.shiftSummaryId, attachmentId));
      this.currentSummary.set(updated);
      this.setSavedAttachments(updated.attachments);
      await this.loadSavedAttachmentPreviewUrls(updated.attachments);
      this.validationError.set(null);
    } catch (error) {
      console.error('Error removing attachment', error);
      this.validationError.set('Unable to remove attachment.');
    }
  }

  async downloadAttachment(attachmentId: string | undefined): Promise<void> {
    if (!this.shiftSummaryId || !attachmentId) {
      return;
    }

    try {
      const url = await firstValueFrom(
        this.shiftSummaryClient.getAttachmentDownloadUrl(this.shiftSummaryId, attachmentId)
      );
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error downloading attachment', error);
    }
  }

  openDashboard(url: string | undefined): void {
    if (url) {
      window.open(url, '_blank');
    }
  }

  cancel(): void {
    this.router.navigate(['/vendorjob', this.jobId, 'shiftsummaries']);
  }

  formatNA(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }

    return String(value);
  }

  formatYesNo(value: boolean | null | undefined): string {
    if (value === true) {
      return 'Yes';
    }

    if (value === false) {
      return 'No';
    }

    return 'N/A';
  }

  formatPercentage(value: number | undefined | null): string {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    return `${value.toFixed(2)}%`;
  }

  formatNumber(value: number | undefined | null, decimals: number = 2): string {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    return value.toFixed(decimals);
  }

  private async saveDraftInternal(navigateAfterCreate: boolean = true): Promise<ShiftSummary | null> {
    if (this.isReadOnly()) {
      return this.currentSummary();
    }

    if (!this.validateForSave()) {
      return null;
    }

    this.saving.set(true);
    try {
      const payload = this.buildPayload();
      const wasNew = !this.shiftSummaryId;

      const saved = this.shiftSummaryId
        ? await firstValueFrom(this.shiftSummaryClient.update(this.shiftSummaryId, payload))
        : await firstValueFrom(this.shiftSummaryClient.create(payload));

      if (!saved.id) {
        throw new Error('Saved summary did not return an id.');
      }

      this.shiftSummaryId = saved.id;
      await this.persistDirtyAttachmentDescriptions(saved.id);
      await this.uploadPendingFiles(saved.id);
      await this.loadSummary(saved.id);

      if (wasNew && navigateAfterCreate) {
        await this.router.navigate(['/vendorjob', this.jobId, 'shiftsummaries', saved.id], { replaceUrl: true });
      }

      this.validationError.set(null);
      return this.currentSummary();
    } catch (error) {
      console.error('Error saving shift summary', error);
      this.validationError.set('Unable to save shift summary. Please try again.');
      return null;
    } finally {
      this.saving.set(false);
    }
  }

  private async loadSummary(id: string): Promise<void> {
    this.loading.set(true);
    try {
      const summary = await firstValueFrom(this.shiftSummaryClient.getById(id));

      if (summary.isSubmitted) {
        await this.router.navigate(['/vendorjob', this.jobId, 'shiftsummaries', id, 'view'], { replaceUrl: true });
        return;
      }

      this.currentSummary.set(summary);
      this.shiftSummaryId = summary.id ?? id;
      this.setSavedAttachments(summary.attachments);
      await this.loadSavedAttachmentPreviewUrls(summary.attachments);
      this.mapSummaryToForm(summary);
      await this.loadSelectedCrewMembersMissingFromCompanyOptions();
      this.refreshPersonnelOptions();
      this.validationError.set(null);
    } catch (error) {
      console.error('Error loading shift summary', error);
      this.validationError.set('Unable to load shift summary.');
    } finally {
      this.loading.set(false);
    }
  }

  private async loadJobContext(): Promise<void> {
    if (!this.jobId) {
      return;
    }

    try {
      const job = await firstValueFrom(this.vendorJobClient.getVendorJobById(this.jobId));
      this.jobWorkbookType.set(job.workbookType ?? null);
      await this.loadPersonnelOptions(job);
    } catch (error) {
      console.error('Error loading shift summary job context', error);
      this.validationError.set('Unable to load job personnel for this shift summary.');
    }
  }

  private async loadPersonnelOptions(job: VendorJob): Promise<void> {
    if (!job.companyId) {
      this.crewMemberPersonnelOptions = [];
      this.refreshPersonnelOptions();
      return;
    }

    try {
      const crewMembers = await firstValueFrom(this.userClient.getCompanyUserNames(job.companyId));

      this.crewMemberPersonnelOptions = this.toCrewMemberOptions(crewMembers);
      await this.loadSelectedCrewMembersMissingFromCompanyOptions();
    } catch (error) {
      console.error('Error loading shift summary company personnel', error);
      this.crewMemberPersonnelOptions = [];
    }

    this.refreshPersonnelOptions();
  }

  private refreshPersonnelOptions(): void {
    const options = buildPersonnelOptions({
      selectedCrewMemberUserIds: this.selectedCrewMemberUserIds,
      crewMemberPersonnelOptions: this.crewMemberPersonnelOptions
    });

    this.crewMemberOptions.set(options.crewMemberOptions);
  }

  private async loadSelectedCrewMembersMissingFromCompanyOptions(): Promise<void> {
    const loadedUserIds = new Set(this.crewMemberPersonnelOptions.map(option => option.userId));
    const missingUserIds = this.selectedCrewMemberUserIds.filter(userId => userId && !loadedUserIds.has(userId));

    if (!missingUserIds.length) {
      return;
    }

    try {
      const selectedUsers = await firstValueFrom(this.userClient.getUserNames2(missingUserIds.join(',')));
      this.crewMemberPersonnelOptions = [
        ...this.crewMemberPersonnelOptions,
        ...this.toCrewMemberOptions(selectedUsers)
      ];
    } catch (error) {
      console.error('Error loading saved shift summary crew members', error);
    }
  }

  private toCrewMemberOptions(users: UserNameViewModel[] | null | undefined): ShiftSummaryCrewMemberOption[] {
    const byUserId = new Map<string, ShiftSummaryCrewMemberOption>();

    for (const user of users ?? []) {
      const userId = user.userId?.trim();
      const userName = user.userName?.trim();
      if (!userId || !userName || byUserId.has(userId)) {
        continue;
      }

      byUserId.set(userId, { userId, userName });
    }

    return Array.from(byUserId.values());
  }

  private async uploadPendingFiles(shiftSummaryId: string): Promise<void> {
    const attachments = this.pendingAttachments();
    if (!attachments.length) {
      return;
    }

    for (const pendingAttachment of attachments) {
      const file = pendingAttachment.file;
      try {
        const sasUrl = await firstValueFrom(
          this.shiftSummaryClient.getAttachmentUploadUrl(shiftSummaryId, file.name)
        );

        const blockBlobClient = new BlockBlobClient(sasUrl);
        await blockBlobClient.uploadBrowserData(file, {
          blobHTTPHeaders: { blobContentType: file.type }
        });

        const attachment = new ShiftSummaryAttachment();
        attachment.fileName = file.name;
        attachment.description = pendingAttachment.description.trim() || undefined;
        attachment.contentType = file.type;
        attachment.sizeBytes = file.size;
        await firstValueFrom(this.shiftSummaryClient.addAttachment(shiftSummaryId, attachment));
      } catch (error) {
        console.error(`Error uploading file ${file.name}`, error);
      }
    }

    this.clearPendingAttachments();
    this.fileUploader?.clear();
  }

  private async persistDirtyAttachmentDescriptions(shiftSummaryId: string): Promise<void> {
    const dirtyAttachments = this.savedAttachments()
      .filter(attachment => !!attachment.id && this.hasSavedAttachmentDescriptionChanged(attachment))
      .map(attachment => ({
        id: attachment.id!,
        description: attachment.description?.trim() || undefined
      }));

    for (const dirtyAttachment of dirtyAttachments) {
      const payload = new ShiftSummaryAttachment();
      payload.description = dirtyAttachment.description;

      const updated = await firstValueFrom(
        this.shiftSummaryClient.updateAttachment(shiftSummaryId, dirtyAttachment.id, payload)
      );
      this.currentSummary.set(updated);
    }
  }

  private mapSummaryToForm(summary: ShiftSummary): void {
    this.shiftStartUtc = summary.shiftStartUtc ? new Date(summary.shiftStartUtc) : null;
    this.shiftEndUtc = summary.shiftEndUtc ? new Date(summary.shiftEndUtc) : null;
    this.dayNight = summary.dayNight ?? 'Day';
    this.selectedCrewMemberUserIds = (summary.crewMemberUserIds ?? [])
      .map(userId => userId?.trim())
      .filter((userId): userId is string => !!userId);
    this.currentStatus = summary.currentStatus ?? '';
    this.safetyTopicsDiscussed = summary.safetyTopicsDiscussed ?? '';
    this.anySafetyIncidents = summary.anySafetyIncidents ?? false;
    this.safetyIncidentDetails = summary.safetyIncidentDetails ?? '';
    this.safetyIncidents = summary.safetyIncidents ?? [];
    this.jobStatus = summary.jobStatus ?? '';
    this.customerFeedback = summary.customerFeedback ?? '';
    this.mechanicalNeeds = summary.mechanicalNeeds ?? '';
    this.nextPlannedWork = summary.nextPlannedWork ?? '';
    this.dataComponentsWorking = summary.dataComponentsWorking ?? false;
    this.dataComponentsContext = summary.dataComponentsContext ?? '';
    this.fluidType = summary.fluidType ?? '';
    this.currentFluidSystem = summary.currentFluidSystem ?? '';
    this.foamStabilityMinutes = summary.foamStabilityMinutes ?? null;
    this.sandSuspensionMinutes = summary.sandSuspensionMinutes ?? null;
    this.partCTestStatus = summary.partCTestStatus ?? '';
    this.gramsToPass = summary.gramsToPass ?? null;
    this.waterLeakOff = summary.waterLeakOff ?? null;
    this.iscavGallonsConsumed = summary.iscavGallonsConsumed ?? null;
    this.procideGallonsConsumed = summary.procideGallonsConsumed ?? null;
    this.fuelProvidedByCustomer = summary.fuelProvidedByCustomer ?? false;
  }

  private buildPayload(): ShiftSummary {
    return buildShiftSummaryPayload({
      jobId: this.jobId,
      summaryProfile: this.activeSummaryProfile(),
      shiftStartUtc: this.shiftStartUtc,
      shiftEndUtc: this.shiftEndUtc,
      dayNight: this.dayNight,
      selectedCrewMemberUserIds: this.selectedCrewMemberUserIds,
      crewMemberOptions: this.crewMemberPersonnelOptions,
      currentStatus: this.currentStatus,
      safetyTopicsDiscussed: this.safetyTopicsDiscussed,
      anySafetyIncidents: this.anySafetyIncidents,
      safetyIncidentDetails: this.safetyIncidentDetails,
      safetyIncidents: this.safetyIncidents,
      jobStatus: this.jobStatus,
      customerFeedback: this.customerFeedback,
      mechanicalNeeds: this.mechanicalNeeds,
      nextPlannedWork: this.nextPlannedWork,
      dataComponentsWorking: this.dataComponentsWorking,
      dataComponentsContext: this.dataComponentsContext,
      fluidType: this.fluidType,
      currentFluidSystem: this.currentFluidSystem,
      foamStabilityMinutes: this.foamStabilityMinutes,
      sandSuspensionMinutes: this.sandSuspensionMinutes,
      partCTestStatus: this.partCTestStatus,
      gramsToPass: this.gramsToPass,
      waterLeakOff: this.waterLeakOff,
      iscavGallonsConsumed: this.iscavGallonsConsumed,
      procideGallonsConsumed: this.procideGallonsConsumed,
      fuelProvidedByCustomer: this.fuelProvidedByCustomer
    });
  }

  private validateForSave(): boolean {
    const validationMessage = validateShiftSummaryForSave({
      shiftStartUtc: this.shiftStartUtc,
      shiftEndUtc: this.shiftEndUtc
    });
    this.validationError.set(validationMessage);
    return !validationMessage;
  }

  private validateForSubmit(): boolean {
    const validationMessage = validateShiftSummaryForSubmit({
      jobId: this.jobId,
      summaryProfile: this.activeSummaryProfile(),
      shiftStartUtc: this.shiftStartUtc,
      shiftEndUtc: this.shiftEndUtc,
      dayNight: this.dayNight,
      selectedCrewMemberUserIds: this.selectedCrewMemberUserIds,
      crewMemberOptions: this.crewMemberPersonnelOptions,
      currentStatus: this.currentStatus,
      safetyTopicsDiscussed: this.safetyTopicsDiscussed,
      anySafetyIncidents: this.anySafetyIncidents,
      safetyIncidentDetails: this.safetyIncidentDetails,
      safetyIncidents: this.safetyIncidents,
      jobStatus: this.jobStatus,
      customerFeedback: this.customerFeedback,
      mechanicalNeeds: this.mechanicalNeeds,
      nextPlannedWork: this.nextPlannedWork,
      dataComponentsWorking: this.dataComponentsWorking,
      dataComponentsContext: this.dataComponentsContext,
      fluidType: this.fluidType,
      currentFluidSystem: this.currentFluidSystem,
      foamStabilityMinutes: this.foamStabilityMinutes,
      sandSuspensionMinutes: this.sandSuspensionMinutes,
      partCTestStatus: this.partCTestStatus,
      gramsToPass: this.gramsToPass,
      waterLeakOff: this.waterLeakOff,
      iscavGallonsConsumed: this.iscavGallonsConsumed,
      procideGallonsConsumed: this.procideGallonsConsumed,
      fuelProvidedByCustomer: this.fuelProvidedByCustomer
    });
    this.validationError.set(validationMessage);
    return !validationMessage;
  }

  private confirmShiftTimeIfNeeded(acceptLabel: string): Promise<boolean> {
    const warningMessages = getShiftTimeWarningMessages({
      shiftStartUtc: this.shiftStartUtc,
      shiftEndUtc: this.shiftEndUtc,
      dayNight: this.dayNight
    });

    if (!warningMessages.length) {
      return Promise.resolve(true);
    }

    return new Promise(resolve => {
      this.confirmationService.confirm({
        key: 'confirmationDialog',
        header: 'Review Shift Times',
        message: `${warningMessages.join(' ')} Do you want to continue with the selected times?`,
        icon: 'pi pi-exclamation-triangle',
        acceptLabel,
        rejectLabel: 'Cancel',
        accept: () => resolve(true),
        reject: () => resolve(false)
      });
    });
  }

  private hasAnyAttachments(): boolean {
    return this.pendingAttachments().length > 0 || this.savedAttachments().length > 0;
  }

  private createPendingAttachment(file: File): PendingAttachment {
    const previewUrl = isImageAttachment(file.type, file.name)
      ? URL.createObjectURL(file)
      : null;

    return {
      id: `pending-${++this.pendingAttachmentSequence}`,
      file,
      description: '',
      isImage: !!previewUrl,
      previewUrl
    };
  }

  private removePendingAttachmentByFile(fileToRemove: File): void {
    this.pendingAttachments.update(attachments => {
      const toRemove = attachments.find(attachment => this.areFilesEqual(attachment.file, fileToRemove));
      if (toRemove?.previewUrl) {
        URL.revokeObjectURL(toRemove.previewUrl);
      }

      return attachments.filter(attachment => !this.areFilesEqual(attachment.file, fileToRemove));
    });
  }

  private clearPendingAttachments(): void {
    this.pendingAttachments().forEach(attachment => {
      if (attachment.previewUrl) {
        URL.revokeObjectURL(attachment.previewUrl);
      }
    });

    this.pendingAttachments.set([]);
  }

  private areFilesEqual(left: File, right: File): boolean {
    return left.name === right.name
      && left.size === right.size
      && left.lastModified === right.lastModified;
  }

  private setSavedAttachments(attachments: ShiftSummaryAttachment[] | undefined): void {
    const nextAttachments = (attachments ?? []).map(attachment => new ShiftSummaryAttachment(attachment));
    this.savedAttachments.set(nextAttachments);

    const nextIds = new Set(nextAttachments.map(attachment => attachment.id).filter((id): id is string => !!id));
    this.savedAttachmentPreviewUrls.update(current =>
      Object.fromEntries(Object.entries(current).filter(([id]) => nextIds.has(id)))
    );
  }

  private async loadSavedAttachmentPreviewUrls(attachments: ShiftSummaryAttachment[] | undefined): Promise<void> {
    if (!this.shiftSummaryId) {
      this.savedAttachmentPreviewUrls.set({});
      return;
    }

    const imageAttachments = (attachments ?? [])
      .filter(attachment => !!attachment.id && this.isSavedAttachmentImage(attachment));

    if (!imageAttachments.length) {
      this.savedAttachmentPreviewUrls.set({});
      return;
    }

    const previewEntries = await Promise.all(imageAttachments.map(async attachment => {
      try {
        const url = await firstValueFrom(
          this.shiftSummaryClient.getAttachmentDownloadUrl(this.shiftSummaryId!, attachment.id!)
        );
        return [attachment.id!, url] as const;
      } catch (error) {
        console.error('Error loading shift summary attachment preview', error);
        return [attachment.id!, null] as const;
      }
    }));

    this.savedAttachmentPreviewUrls.set(
      Object.fromEntries(previewEntries.filter((entry): entry is readonly [string, string] => !!entry[1]))
    );
  }

  private formatShiftShortcutDate(date: Date): string {
    return this.shiftShortcutLabelFormatter.format(date);
  }

  private addHours(date: Date, hours: number): Date {
    return new Date(date.getTime() + (hours * 60 * 60 * 1000));
  }
}
